import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';
import { bodyStatusError } from '@/util/master/requestError';

const BASE_PATH = '/notification/inbox';

/**
 * `/notification/inbox*` — inbox notifikasi user login, lintas SEMUA company
 * keanggotaannya, dengan status baca per user. Semua method SUDAH di-unwrap: yang
 * balik body, bukan response axios.
 *
 * Kontrak backend (dicek ke dev server 2026-09-17):
 * - gagal bisnis tetap HTTP 200: `status.error` bukan nol (`true`/1) dan/atau
 *   `status.errorCode` bukan nol. Sukses bawa `status: {error: 0, errorCode: 0}`.
 *   Body gagal di-throw jadi Error ber-`errorCode` lewat bodyStatusError.
 * - exception server (mis. `page=abc`) datang HTTP 500 — axios yang throw, pesannya
 *   di `error.response.data.status.message`.
 * - filter yang gak dikenal (`severity=FOO`, `compId` bukan milik user) bukan error,
 *   hasilnya list kosong. `max` di atas 100 dipotong jadi 100, `max` kosong jadi 20.
 * - waktu ISO 8601 UTC berakhiran `Z`.
 *
 * Count, list navbar, dan aksi baca sengaja TANPA cancel token biar gak ikut
 * dibatalin `router.beforeEach`; list halaman Notifications pakai cancel token.
 */

/**
 * @typedef {Object} InboxItem
 * @property {number} id id baris inbox milik user, dipakai buat tandai dibaca
 * @property {number} itemId id notifikasi sumber, sama buat semua penerima
 * @property {string} topic mis. 'alert.device_offline'
 * @property {'ALERT'|'EVENT'|'REPORT'|'ASSET'|'SYSTEM'} category
 * @property {'CRITICAL'|'WARNING'|'INFO'} severity
 * @property {'START'|'REMINDER'|'RESOLVE'|'SINGLE'} phase
 * @property {string} title teks Inggris siap tampil
 * @property {string} body teks Inggris siap tampil, jangan dirakit ulang
 * @property {number} compId 0 buat notifikasi system-wide
 * @property {string} compName
 * @property {number} massId 0 kalau gak terkait kapal
 * @property {string} massName '' kalau gak terkait kapal
 * @property {string} deviceId mis. 'gateway-lestari_yanindo_57'
 * @property {string} sourceType mis. 'ALERT_INCIDENT'
 * @property {string} sourceId teks, bukan angka
 * @property {string} occurredAt ISO 8601 UTC, mis. '2026-09-16T19:41:29Z'
 * @property {string|null} readAt null = belum dibaca
 * @property {string} link path FE siap `router.push`, '' = gak ada halaman
 * @property {string} groupKey mis. 'alert:1534510'
 * @property {Object} payload data mentah per topic
 */

async function unwrap(request) {
  const { data: body } = await request;
  const error = bodyStatusError(body);
  if (error) throw error;
  return body;
}

export default {
  /**
   * GET `/notification/inbox` — satu halaman inbox, terbaru duluan. Filter di-AND.
   *
   * @param {Object} [params] hasil toInboxParams: `unread` (1), `category`,
   *   `severity`, `compId`, `massId`, `page`, `max`
   * @param {{cancellable?: boolean}} [options] `cancellable: true` = ikut dibatalin
   *   pas pindah route
   * @returns {Promise<{data: InboxItem[], pagination: {page: number, itemPerPage: number,
   *   totalPage: number, data: number, totalData: number},
   *   status: import('@/services/_AxiosInstance').ApiStatus}>}
   * @throws {Error} ber-`errorCode` kalau body bawa status gagal
   */
  getInbox(params = {}, { cancellable = false } = {}) {
    const config = { params };
    if (cancellable) config.cancelToken = getNewCancelToken();
    return unwrap(axiosInstance.get(BASE_PATH, config));
  },

  /**
   * GET `/notification/inbox/count` — angka badge. Field-nya di level atas body,
   * bukan di `data`. `bySeverity` cuma ngitung yang belum dibaca.
   *
   * @returns {Promise<{unread: number, bySeverity: {CRITICAL: number, WARNING: number,
   *   INFO: number}, status: import('@/services/_AxiosInstance').ApiStatus}>}
   */
  getCount() {
    return unwrap(axiosInstance.get(`${BASE_PATH}/count`));
  },

  /**
   * PUT `/notification/inbox/read/{id}` — tandai satu item dibaca. `id` itu
   * `InboxItem.id`, bukan `itemId`.
   *
   * Sudah dibaca atau bukan milik user dibalas HTTP 200 + `errorCode` 404
   * "Notification not found or already read".
   *
   * @param {number} id
   * @returns {Promise<{affected_rows: number, status: import('@/services/_AxiosInstance').ApiStatus}>}
   * @throws {Error} ber-`errorCode` 404 kalau sudah dibaca
   */
  markRead(id) {
    return unwrap(axiosInstance.put(`${BASE_PATH}/read/${id}`));
  },

  /**
   * PUT `/notification/inbox/read-all?before={id}` — tandai dibaca semua item
   * sampai id itu (inklusif), di SEMUA company. Gak nerima filter lain.
   * `affected_rows` 0 bukan error.
   *
   * @param {number} beforeId id item terbaru yang lagi tampil
   * @returns {Promise<{affected_rows: number, status: import('@/services/_AxiosInstance').ApiStatus}>}
   */
  markAllRead(beforeId) {
    return unwrap(axiosInstance.put(`${BASE_PATH}/read-all`, null, { params: { before: beforeId } }));
  },
};
